import React, { useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import CustomModal from "../../../components/customModal/CustomModal";
import CreateBarcodeForm, { CreateBarcodeFormValues, CreateBarcodeFormPrefillData } from "./CreateBarcodeForm";
import moment from "moment";

export interface BarcodeItem {
  id: number;
  barcode: string;
  ndc: string;
  lot: string;
  expDate: string;
  source: string;
  orderingProductNo: string;
}

interface ExistingBarcodesModalProps {
  barcodeItems: BarcodeItem[];
  onSave: (selectedBarcode: BarcodeItem, qty: string, refInfo: string) => void;
  onClose: () => void;
  onNewBarcodeSave: (values: CreateBarcodeFormValues) => void;
  prefillData?: CreateBarcodeFormPrefillData;
}

const ExistingBarcodesModal: React.FC<ExistingBarcodesModalProps> = ({
  barcodeItems,
  onSave,
  onClose,
  onNewBarcodeSave,
  prefillData,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState<BarcodeItem | null>(null);
  const [qty, setQty] = useState(prefillData?.qty || "");
  const [refInfo, setRefInfo] = useState(prefillData?.refInfo || "");
  const [qtyTouched, setQtyTouched] = useState(false);

  const handleRowClick = (barcode: BarcodeItem) => {
    setSelectedBarcode(barcode);
    setQty(prefillData?.qty || "");
    setRefInfo(prefillData?.refInfo || "");
    setQtyTouched(false);
  };

  const handleSave = () => {
    setQtyTouched(true);
    if (!qty) return;
    if (selectedBarcode) {
      onSave(selectedBarcode, qty, refInfo);
    }
  };

  const qtyError = qtyTouched && !qty;

  return (
    <CustomModal
      id="existing-barcodes-modal"
      title="Existing Barcodes"
      overlayClass="existing-barcodes-overlay"
      onClose={onClose}
      content={
        <div className="existing-barcodes-content">
          <Table bordered hover size="sm">
            <thead>
              <tr>
                {/* <th>Id</th> */}
                <th>Barcode</th>
                <th>NDC</th>
                <th>Lot</th>
                <th>Exp Date</th>
                <th>Source</th>
                <th className="text-center">Ordering Product No</th>
              </tr>
            </thead>
            <tbody>
              {barcodeItems.length > 0 ? (
                barcodeItems.map((barcode) => (
                  <tr
                    key={barcode.id}
                    className={selectedBarcode?.id === barcode.id ? "selected-row" : ""}
                    onClick={() => handleRowClick(barcode)}
                    style={{ cursor: "pointer" }}
                  >
                    {/* <td>{barcode.id}</td> */}
                    <td>{barcode.barcode}</td>
                    <td>{barcode.ndc}</td>
                    <td>{barcode.lot}</td>
                    <td>{barcode.expDate ? moment.parseZone(barcode.expDate).format("M/D/YYYY") : ""}</td>
                    <td>{barcode.source}</td>
                    <td className="text-center">{barcode.orderingProductNo}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center"
                    style={{ color: "red", fontWeight: "bold" }}
                  >
                    No matching barcodes found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <p className="record-count">
            {barcodeItems.length} record(s)
          </p>

          {selectedBarcode && !showCreateForm && (
            <div className="selected-barcode-fields">
              <div className="barcode-inline-fields">
                <Form.Group className="inline-field">
                  <Form.Label>
                    Qty: <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    onBlur={() => setQtyTouched(true)}
                    isInvalid={qtyError}
                  />
                  {qtyError && (
                    <Form.Control.Feedback type="invalid">
                      Qty is required
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                <Form.Group className="inline-field">
                  <Form.Label>Ref Info:</Form.Label>
                  <Form.Control
                    size="sm"
                    value={refInfo}
                    onChange={(e) => setRefInfo(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>
          )}

          {showCreateForm ? (
            <CreateBarcodeForm
              onSave={(values) => {
                setShowCreateForm(false);
                onNewBarcodeSave(values);
              }}
              onCancel={() => setShowCreateForm(false)}
              prefillData={prefillData}
            />
          ) : (
            <div className="modal--action--btn--wrapper">
              <Button
                variant="outline-primary"
                onClick={() => setShowCreateForm(true)}
              >
                Create New Barcode
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!selectedBarcode}
              >
                Save
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default ExistingBarcodesModal;
